import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import { useUser } from '@/composables/useUser'

const router = createRouter({
  scrollBehavior(to) {
    // Les liens « compléter mon profil » visent un champ précis (SP-24) : on y mène
    // directement, la barre de navigation collante laissant la place (`scroll-mt`).
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0, left: 0 }
  },
  routes,
  history: createWebHistory(import.meta.env.BASE_URL),
})

const { hydrate, isAuthenticated, isAdmin, mfaBlocking, refreshSession } = useUser()

/** Pages du parcours second facteur : accessibles à une session non encore renforcée. */
const MFA_PAGES = new Set(['mfa', 'mfa-enroll'])

/**
 * Garde d'authentification.
 *
 *  1. session vivante mais profil non hydraté (rechargement, F5) → réhydratation ;
 *  2. page réservée aux visiteurs et compte connecté → accueil ;
 *  3. page protégée sans session → mire, avec le chemin visé en `redirect` ;
 *  4. facteur exigé par la politique et non prouvé → page second facteur ;
 *  5. page réservée à un rôle → accueil si le rôle ne convient pas (l'API tranche).
 */
router.beforeEach(async (to) => {
  const hasSession = refreshSession()

  let hydrationFailed = false
  if (hasSession && !isAuthenticated.value) {
    try {
      await hydrate()
    } catch {
      hydrationFailed = true
    }
  }

  if (to.meta.guestOnly && isAuthenticated.value) return { name: 'home' }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  if (requiresAuth && !isAuthenticated.value) {
    // API injoignable : ne pas boucler entre la mire et la page visée.
    if (hydrationFailed) return { name: 'login', query: { error: 'profile' } }
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (requiresAuth && isAuthenticated.value && mfaBlocking.value && !MFA_PAGES.has(String(to.name))) {
    return { name: 'mfa', query: { redirect: to.fullPath } }
  }

  if (to.matched.some((record) => record.meta.role === 'admin') && !isAdmin.value) return { name: 'home' }
  return true
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · SuiviPMA` : 'SuiviPMA'
})

export default router
